import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Divider } from 'antd';
import { SearchOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { IMenuProps, MenuItemProps, SubMenuProps } from './types';
import { match } from 'pinyin-pro';
import { useTranslation } from 'react-i18next';
interface Props {
  items: IMenuProps[];
  visible: boolean;
  onVisibleChange: (item?: IquickMenuItem) => void;
}

export const QUICKSTART = 'quickstart';
type IquickMenuItem = MenuItemProps & { primaryKey: string; primaryLabel: string; stared: boolean };
const flattenMenu = (items: IMenuProps[]): IquickMenuItem[] => {
  const localCache = JSON.parse(localStorage.getItem(QUICKSTART) || '{}');
  return items
    .filter((i) => !!i)
    .map((item: SubMenuProps) => {
      if (item.children) {
        return item.children
          .filter((i) => !!i)
          .map((i) => ({
            ...item,
            ...i,
            primaryKey: item.key,
            primaryLabel: item.label,
            children: undefined,
            stared: localCache[i.key],
          }));
      } else {
        return { ...item, stared: localCache[item.key], primaryKey: item.key, primaryLabel: item.label };
      }
    })
    .flat(2);
};

const calcVisibleMenuRange = (scrollTop, type: 'menu' | 'starMenu') => {
  const map = { menu: 9, starMenu: 3 };
  const scrollItemNum = Math.floor(scrollTop / 38);
  return [scrollItemNum, scrollItemNum + map[type]];
};

export default function Quickstart(props: Props) {
  const { t, i18n } = useTranslation('fc-menu');
  const { visible, onVisibleChange, items } = props;
  const inputRef = useRef<any>(null);
  const ref = useRef<any>(null);
  const starRef = useRef<any>(null);
  const [search, setSearch] = useState('');
  const [menus, setMenus] = useState<IquickMenuItem[]>(flattenMenu(items));
  const recentMenuKeys: string[] = JSON.parse(localStorage.getItem(QUICKSTART + 'recent') || '[]');
  const [lockMouseHover, setLockMouseHover] = useState(false);
  const recentMenus = recentMenuKeys.map((k) => menus.find((item) => item.key === k)).filter((p) => p);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentActiveIndex, setRecentActiveIndex] = useState(-1);
  const [starActiveIndex, setStarActiveIndex] = useState(-1);
  const displayMenus = menus.filter(
    (menu) =>
      !search ||
      match(menu.label, search) ||
      match(menu.primaryLabel, search) ||
      menu.label.includes(search) ||
      menu.primaryLabel.includes(search),
  );
  const staredMenus = menus.filter((item) => item.stared);
  const handleMenuStar = (key, v, e) => {
    e.stopPropagation();
    const localCache = JSON.parse(localStorage.getItem(QUICKSTART) || '{}');
    localCache[key] = v;
    localStorage.setItem(QUICKSTART, JSON.stringify(localCache));
    setMenus(flattenMenu(items));
  };
  const handleKeyPress = (e) => {
    setLockMouseHover(true);
    switch (e.key) {
      case 'ArrowDown':
        setActiveIndex((activeIndex) => {
          if (activeIndex === -1) return -1;
          const i = activeIndex < displayMenus.length - 1 ? activeIndex + 1 : activeIndex;
          const visibleRange = calcVisibleMenuRange(ref.current.scrollTop, 'menu');
          if (i > visibleRange[1]) {
            ref.current.scrollTop = ref.current.scrollTop + 38;
          }
          return i;
        });
        setStarActiveIndex((starActiveIndex) => {
          if (starActiveIndex === -1) return -1;
          const i = starActiveIndex < staredMenus.length - 1 ? starActiveIndex + 1 : starActiveIndex;
          const visibleRange = calcVisibleMenuRange(starRef.current.scrollTop, 'starMenu');
          if (i > visibleRange[1]) {
            starRef.current.scrollTop = starRef.current.scrollTop + 38;
          }
          return i;
        });
        setRecentActiveIndex((recentActiveIndex) => {
          if (recentActiveIndex === -1) return -1;
          if (recentActiveIndex === recentMenuKeys.length - 1) {
            setStarActiveIndex(0);
            starRef.current.scrollTop = 0;
            return -1;
          }
          return recentActiveIndex < recentMenuKeys.length - 1 ? recentActiveIndex + 1 : recentActiveIndex;
        });

        break;
      case 'ArrowUp':
        setActiveIndex((activeIndex) => {
          if (activeIndex === -1) return -1;
          const i = activeIndex == 0 ? 0 : activeIndex - 1;
          const visibleRange = calcVisibleMenuRange(ref.current.scrollTop, 'menu');
          if (i < visibleRange[0]) {
            ref.current.scrollTop = ref.current.scrollTop - 38;
          }
          return i;
        });
        setRecentActiveIndex((recentActiveIndex) => {
          if (recentActiveIndex === -1) return -1;
          return recentActiveIndex == 0 ? 0 : recentActiveIndex - 1;
        });
        setStarActiveIndex((starActiveIndex) => {
          if (starActiveIndex === -1) return -1;
          if (starActiveIndex === 0) {
            starRef.current.scrollTop = 0;
            setRecentActiveIndex(recentMenuKeys.length - 1);
            return -1;
          }
          const i = starActiveIndex == 0 ? 0 : starActiveIndex - 1;
          const visibleRange = calcVisibleMenuRange(starRef.current.scrollTop, 'starMenu');
          if (i < visibleRange[0]) {
            starRef.current.scrollTop = starRef.current.scrollTop - 38;
          }
          return i;
        });
        break;
      case 'ArrowRight':
        if (activeIndex === -1) return;
        setActiveIndex(-1);
        setRecentActiveIndex(0);
        break;
      case 'ArrowLeft':
        if (activeIndex > -1) return;
        setActiveIndex(0);
        setRecentActiveIndex(-1);
        setStarActiveIndex(-1);
        ref.current.scrollTop = 0;
        break;
      case 'Enter':
        if (activeIndex > -1) {
          handleClick(displayMenus[activeIndex]);
        }
        if (recentActiveIndex > -1) {
          handleClick(recentMenus[recentActiveIndex] as IquickMenuItem);
        }
        if (starActiveIndex > -1) {
          handleClick(staredMenus[starActiveIndex]);
        }
        break;
      default:
        break;
    }
  };

  const handleClick = (item: IquickMenuItem) => {
    onVisibleChange(item);

    localStorage.setItem(
      QUICKSTART + 'recent',
      JSON.stringify([item.key, ...recentMenuKeys.filter((i) => i !== item.key)].slice(0, 5)),
    );
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [displayMenus, recentMenuKeys, staredMenus]);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  return (
    <Modal
      wrapClassName='quick-start-modal'
      mask={false}
      title={null}
      closable={false}
      footer={null}
      visible={visible}
      width={780}
      onOk={() => onVisibleChange()}
      onCancel={() => onVisibleChange()}
    >
      <Input
        ref={inputRef}
        size='large'
        prefix={<SearchOutlined style={{ color: '#666' }} />}
        value={search}
        onChange={(e) => {
          setActiveIndex(0);
          setRecentActiveIndex(-1);
          setStarActiveIndex(-1);
          setSearch(e.target.value);
        }}
      />
      <div style={{ display: 'flex', marginTop: 8 }} onMouseMove={() => setLockMouseHover(false)}>
        <div style={{ height: 418, flex: 1 }}>
          <div className='quick-menu-title'>{t('快捷菜单')}</div>
          <div style={{ overflow: 'auto', height: 380 }} ref={ref}>
            {displayMenus.map((item, i) => (
              <div
                className={i === activeIndex ? 'quick-menu-item active' : 'quick-menu-item'}
                key={item.key}
                onMouseOver={() => {
                  if (lockMouseHover) return;
                  setActiveIndex(i);
                  setStarActiveIndex(-1);
                  setRecentActiveIndex(-1);
                }}
                onClick={() => handleClick(item)}
              >
                <span style={{ display: 'flex' }}>
                  <span className='quick-menu-icon'>{item.icon}</span>
                  <span className='primary-menu'>{item.primaryLabel}</span>
                  <span className='secondary-menu'>{item.label}</span>
                </span>
                {item.stared ? (
                  <StarFilled className='unstar' onClick={(e) => handleMenuStar(item.key, 0, e)} />
                ) : (
                  <StarOutlined className='stared' onClick={(e) => handleMenuStar(item.key, 1, e)} />
                )}
              </div>
            ))}
          </div>
        </div>
        <Divider type='vertical' style={{ height: 418 }} />
        <div style={{ height: 418, flex: 1 }}>
          <div className='quick-menu-title'>{t('近期使用')}</div>
          <div style={{ overflow: 'auto', height: 190 }}>
            {recentMenus.map((item: IquickMenuItem, i) => (
              <div
                className={i === recentActiveIndex ? 'quick-menu-item active' : 'quick-menu-item'}
                key={item.key}
                onMouseOver={() => {
                  if (lockMouseHover) return;
                  setRecentActiveIndex(i);
                  setActiveIndex(-1);
                  setStarActiveIndex(-1);
                }}
                onClick={() => handleClick(item)}
              >
                <span style={{ display: 'flex' }}>
                  <span className='quick-menu-icon'>{item.icon}</span>
                  <span className='primary-menu'>{item.primaryLabel}</span>
                  <span className='secondary-menu'>{item.label}</span>
                </span>
                {item.stared ? (
                  <StarFilled className='unstar' onClick={(e) => handleMenuStar(item.key, 0, e)} />
                ) : (
                  <StarOutlined className='stared' onClick={(e) => handleMenuStar(item.key, 1, e)} />
                )}
              </div>
            ))}
          </div>
          <div className='quick-menu-title'>{t('个人收藏')}</div>
          <div style={{ overflow: 'auto', height: 152 }} ref={starRef}>
            {staredMenus.map((item: IquickMenuItem, i) => (
              <div
                className={i === starActiveIndex ? 'quick-menu-item active' : 'quick-menu-item'}
                key={item.key}
                onMouseOver={() => {
                  if (lockMouseHover) return;
                  setStarActiveIndex(i);
                  setRecentActiveIndex(-1);
                  setActiveIndex(-1);
                }}
                onClick={() => handleClick(item)}
              >
                <span style={{ display: 'flex' }}>
                  <span className='quick-menu-icon'>{item.icon}</span>
                  <span className='primary-menu'>{item.primaryLabel}</span>
                  <span className='secondary-menu'>{item.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
